import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import type { EstimationAccuracyPeriod } from '../../../services/apiService';
import { useEstimationUnit } from './estimationUnit';

interface Props {
  rows: EstimationAccuracyPeriod[];
  view: 'release' | 'quarter';
  /** Project-wide target deviation percentage (default 10). */
  targetDeviationPct?: number;
}

function fmt(value: number, isDays: boolean): string {
  return (isDays ? value / 8 : value).toFixed(1);
}

function fmtSigned(value: number, isDays: boolean): string {
  const v = isDays ? value / 8 : value;
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}`;
}

/**
 * "Table by period" at the bottom of the Estimation accuracy tab. Reads the
 * shared `EstimationUnit` context so hour columns flip to days alongside the
 * trend chart and period summary. Collapsed by default; expand to see the full table.
 */
export const EstimationByPeriodTable: React.FC<Props> = ({
  rows,
  view,
  targetDeviationPct = 10,
}) => {
  const { unit, parenSuffix } = useEstimationUnit();
  const isDays = unit === 'days';
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((v) => !v);
  const viewLabel = view === 'release' ? 'Per release (fix version)' : 'Per quarter';

  return (
    <div
      className={`estimation-table-card chart-card${expanded ? ' is-expanded' : ' is-collapsed'}`}
    >
      <div className="chart-header estimation-table-card__header">
        <div
          role="button"
          tabIndex={0}
          className="estimation-table-card__toggle"
          aria-expanded={expanded}
          aria-controls="estimation-by-period-body"
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            }
          }}
        >
          {expanded ? (
            <ChevronDown size={20} className="estimation-table-card__chevron" />
          ) : (
            <ChevronRight size={20} className="estimation-table-card__chevron" />
          )}
          <div className="estimation-table-card__title">
            <h3>
              Table by period
              <span
                className="stat-hint-icon"
                title={`Shows how accurate your estimates were for each release or quarter. Compare estimated hours vs logged hours, see deviation and the share of stories within ±${targetDeviationPct}%. Use it to spot problematic releases, improve estimation over time, plan resources better, and discuss timelines with stakeholders.`}
                aria-label="What this table is for"
                onClick={(e) => e.stopPropagation()}
              >
                <Info size={14} />
              </span>
            </h3>
            <p>
              {expanded
                ? `${viewLabel} · ${rows.length} period${rows.length === 1 ? '' : 's'}`
                : 'Click to expand the full table.'}
            </p>
          </div>
        </div>
      </div>
      {expanded && (
        <div id="estimation-by-period-body" className="estimation-table-wrap">
          <table className="estimation-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Stories</th>
                <th>Estimate {parenSuffix}</th>
                <th>Logged {parenSuffix}</th>
                <th>Deviation {parenSuffix}</th>
                <th>Deviation (%)</th>
                <th>Within ±{targetDeviationPct}%</th>
                <th>Avg deviation %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.period}>
                  <td>{row.period}</td>
                  <td>{row.storyCount}</td>
                  <td>{fmt(row.totalEstimateHours, isDays)}</td>
                  <td>{fmt(row.totalLoggedHours, isDays)}</td>
                  <td>
                    <span
                      className={
                        (row.deviationHours ?? 0) >= 0 ? 'deviation-over' : 'deviation-under'
                      }
                    >
                      {fmtSigned(row.deviationHours ?? 0, isDays)}
                    </span>
                  </td>
                  <td>
                    {row.deviationPct != null
                      ? `${row.deviationPct >= 0 ? '+' : ''}${row.deviationPct}%`
                      : '—'}
                  </td>
                  <td>
                    <span
                      className={
                        row.within10PercentPct >= 80
                          ? 'within-good'
                          : row.within10PercentPct >= 50
                            ? 'within-ok'
                            : 'within-low'
                      }
                    >
                      {row.within10PercentPct}%
                    </span>
                    <span className="estimation-count">
                      {' '}
                      ({row.within10PercentCount}/{row.storyCount})
                    </span>
                  </td>
                  <td>{row.avgDeviationPct != null ? `${row.avgDeviationPct}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
